// Profile, as Account.dc.html draws it: their initials in the 72px gold ring, Name and Where you
// invest side by side, the Email they log in with, and Save changes with "Saved" beside it until
// the next edit. For Accelerator members (anyone with the Accelerator in their library, open, paused
// or suspended) Change photo sits beside the ring, and About you and the switch for the Accelerator's
// member directory come under Email, as the board draws them (Adam, 2 Oct: back for the Group page's
// directory, which shows the members of the whole Accelerator, every group, who switch it on; Adam's
// answer 1 of 7 Oct). A member of any other mastermind gets the same, with a switch for each mastermind
// they hold, each its own directory (the product-types follow-ups, 13 Oct): the Accelerator's is
// people.directory_opt_in, saved as before, and another's is their row in hub.directory_listings, read
// only for a member who holds 1 (so an Accelerator member's Account asks and sends what it did). Everyone
// else sees the board without those 3, as since stage 3. The ring stays for everyone.
//
// Changing the email (the board's "Changing it sends a code to the new address first"): Save
// changes with a new address asks change-email for an 8-digit code, which the hub sends to the new
// address itself through Resend. The code step opens under the field; the change happens only once
// the code is confirmed, and then the session is refreshed so it carries the new address. Until
// then the field shows the address they still log in with, the code step names the new one, and
// Save changes is greyed out, so Change email is the step's 1 black button (Adam, 3 Oct). A login
// shared with POYT's site (since migration 82, not the Accelerator's old one) can't change its email
// here (C37): the field stays as it is, with the line that sends them to Adam. The code is confirmed
// once per click: a second
// Enter or click while the first is on its way sends nothing (change-email claims the change too,
// so 2 tabs can't both change it).
import { useEffect, useRef, useState } from 'react'
import * as f from '../lib/format.js'
import { EMAIL_SHAPE } from '../lib/auth.js'
import { supabase } from '../lib/supabase.js'
import { COPY, lineFor, directoryWords } from './copy.js'
import { savePerson, callMember, loadListings, setListing } from './load.js'
import { ProfilePhoto } from './Photo.jsx'
import { useMember } from '../app/member.jsx'
import { useNow } from '../lib/clock.js'
import { ownedProducts } from '../library/model.js'

const sameEmail = (a, b) => String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase()

// Whether they have the Accelerator (a mastermind) in their library: open, overdue, paused or
// suspended, from the Library's own rows
export function hasMastermind(lib, nowMs) {
  return mastermindsOf(lib, nowMs).length > 0
}

// The masterminds in their library, in its order (the product-types plan, 13 Oct: any mastermind, not
// only the Accelerator, has a member directory)
export function mastermindsOf(lib, nowMs) {
  if (!lib) return []
  return [...ownedProducts(lib, nowMs).values()].filter(o => o.product.type === 'mastermind').map(o => o.product)
}

export default function Profile({ person, shared, emailChange, readOnly, onPerson, onEmailChanged, onShared }) {
  const [name, setName] = useState(person.full_name)
  const [loc, setLoc] = useState(person.location || '')
  const [about, setAbout] = useState(person.about || '')
  const [listed, setListed] = useState(Boolean(person.directory_opt_in))
  const { lib } = useMember()
  const nowMs = useNow()
  const masterminds = mastermindsOf(lib?.data, nowMs)
  const acc = masterminds.length > 0
  const dirWords = acc ? directoryWords(masterminds) : null
  // the other masterminds' switches: their rows as saved, and as switched here (product ids)
  const others = (dirWords?.switches || []).filter(w => !w.accelerator).map(w => w.id).join(',')
  const [rows, setRows] = useState({ status: 'idle', saved: [], on: [] })
  useEffect(() => {
    if (!others) return undefined
    let live = true
    setRows(r => ({ ...r, status: 'loading' }))
    loadListings(person.id)
      .then(ids => { if (live) setRows({ status: 'ready', saved: ids, on: ids }) })
      .catch(() => { if (live) setRows({ status: 'error', saved: [], on: [] }) })
    return () => { live = false }
  }, [person.id, others])
  const [email, setEmail] = useState(!shared && emailChange ? emailChange.new_email : person.email)
  const [saved, setSaved] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  // the email change: idle, code (waiting for the code) or done
  const [mail, setMail] = useState(() => (!shared && emailChange
    ? { step: 'code', to: emailChange.new_email, line: COPY.codeWaiting(emailChange.new_email, f.time(emailChange.expires_at)), error: '', resent: false }
    : { step: 'idle', error: '' }))
  const [code, setCode] = useState('')
  const [codeBusy, setCodeBusy] = useState(false)
  // React state lands after the event: 2 key events in the same tick both see codeBusy false, so
  // the ref is what stops a second send
  const codeSending = useRef(false)
  const saving = useRef(false)
  const emailRef = useRef(null)
  const codeRef = useRef(null)
  const focusCode = useRef(false)

  useEffect(() => {
    if (mail.step === 'code' && focusCode.current) { focusCode.current = false; codeRef.current?.focus() }
  }, [mail.step])

  function edited(setter) {
    return e => { setter(e.target.value); setSaved(false); setError('') }
  }

  async function requestCode(to, resend) {
    try {
      const d = await callMember('change-email', { action: 'request', email: to })
      const next = d.email || to
      focusCode.current = !resend
      setMail({ step: 'code', to: next, line: d.message || COPY.codeSent(next), error: '', resent: Boolean(resend) })
      setEmail(next)
      setCode('')
      return true
    } catch (e) {
      if (e.code === 'shared_login') onShared()
      setMail(m => ({ ...m, error: lineFor(e) }))
      return false
    }
  }

  async function save(e) {
    e.preventDefault()
    if (saving.current || readOnly || mail.step === 'code') return
    setError('')
    setSaved(false)
    setMail(m => ({ ...m, error: '' }))
    const fullName = name.trim()
    const location = loc.trim()
    if (!fullName) { setError(COPY.nameEmpty); return }
    const newEmail = email.trim()
    const emailChanged = !shared && mail.step !== 'code' && !sameEmail(newEmail, person.email)
    if (emailChanged && !EMAIL_SHAPE.test(newEmail)) { setMail(m => ({ ...m, step: 'idle', error: COPY.emailInvalid })); return }
    const aboutText = about.trim()
    if (acc && aboutText.length > 400) { setError(COPY.aboutLength); return }
    const aboutChanged = acc && (aboutText || null) !== (person.about || null)
    const listedChanged = acc && listed !== Boolean(person.directory_opt_in)
    const profileChanged = fullName !== person.full_name || (location || null) !== (person.location || null) || aboutChanged || listedChanged
    // another mastermind's switch: its row added or taken away, after the person row
    const rowChanges = rows.status === 'ready' ? (dirWords?.switches || []).filter(w => !w.accelerator)
      .filter(w => rows.on.includes(w.id) !== rows.saved.includes(w.id)).map(w => ({ id: w.id, on: rows.on.includes(w.id) })) : []

    saving.current = true
    setBusy(true)
    try {
      if (profileChanged) {
        try {
          const patch = { full_name: fullName, location: location || null }
          if (aboutChanged) patch.about = aboutText || null
          if (listedChanged) patch.directory_opt_in = listed
          const row = await savePerson(person.id, patch)
          onPerson(row)
          setName(row.full_name)
          setLoc(row.location || '')
          setAbout(row.about || '')
          setListed(Boolean(row.directory_opt_in))
          setSaved(true)
        } catch {
          setError(COPY.saveFailed)
          return
        }
      }
      if (rowChanges.length) {
        try {
          for (const c of rowChanges) {
            await setListing(person.id, c.id, c.on)
            setRows(r => ({ ...r, saved: c.on ? [...r.saved, c.id] : r.saved.filter(x => x !== c.id) }))
          }
          setSaved(true)
        } catch {
          setSaved(false)
          setError(COPY.saveFailed)
          return
        }
      }
      if (emailChanged) await requestCode(newEmail, false)
      else if (!profileChanged) setSaved(true)
    } finally {
      saving.current = false
      setBusy(false)
    }
  }

  async function confirm() {
    if (codeSending.current || readOnly) return
    const c = code.replace(/\s+/g, '')
    if (!/^\d{8}$/.test(c)) { setMail(m => ({ ...m, error: COPY.codeBad })); return }
    codeSending.current = true
    setCodeBusy(true)
    setMail(m => ({ ...m, error: '' }))
    try {
      const d = await callMember('change-email', { action: 'confirm', code: c })
      // the token carries the email, so it's refreshed; the login itself is unchanged
      await supabase.auth.refreshSession().catch(() => null)
      const now = d.email || mail.to
      setEmail(now)
      setCode('')
      setMail({ step: 'done', line: d.message || COPY.emailDone(now), error: '' })
      onEmailChanged()
    } catch (e) {
      if (e.code === 'shared_login') onShared()
      setMail(m => ({ ...m, error: lineFor(e) }))
    } finally {
      codeSending.current = false
      setCodeBusy(false)
    }
  }

  async function resend() {
    if (codeSending.current || readOnly) return
    codeSending.current = true
    setCodeBusy(true)
    setMail(m => ({ ...m, error: '' }))
    await requestCode(mail.to, true)
    codeSending.current = false
    setCodeBusy(false)
  }

  function differentEmail() {
    setMail({ step: 'idle', error: '' })
    setCode('')
    setTimeout(() => emailRef.current?.focus(), 0)
  }

  // 1 mastermind's directory switch: the Accelerator's is directory_opt_in, as before; another's is its row,
  // and can't be switched until the rows are read
  function switchRow(w) {
    const on = w.accelerator ? listed : rows.on.includes(w.id)
    const ready = w.accelerator || rows.status === 'ready'
    const flip = () => {
      if (readOnly || !ready) return
      if (w.accelerator) setListed(v => !v)
      else setRows(r => ({ ...r, on: r.on.includes(w.id) ? r.on.filter(x => x !== w.id) : [...r.on, w.id] }))
      setSaved(false)
      setError('')
    }
    return (
      <button key={w.id} type="button" className="ac-switch-row" aria-pressed={on} disabled={readOnly || !ready} onClick={flip}>
        <span className="ac-switch-label">{w.label}</span>
        <span className={on ? 'ac-toggle ac-toggle--on' : 'ac-toggle'} aria-hidden="true"><span className="ac-toggle-knob" /></span>
      </button>
    )
  }

  const waiting = mail.step === 'code'
  return (
    <section id="profile" className="ac-card ac-card--profile" aria-labelledby="profile-h">
      <h2 id="profile-h" className="ac-h2">{COPY.profile}</h2>
      {/* the board's 72px ring, with Change photo beside it for Accelerator members */}
      <ProfilePhoto person={person} canChange={acc} readOnly={readOnly} onPerson={onPerson} />
      <form className="ac-form" onSubmit={save} noValidate>
        <div className="ac-grid2">
          <div className="ac-field">
            <label htmlFor="ac-name" className="ac-label">{COPY.name}</label>
            <input id="ac-name" className="ac-input" value={name} onChange={edited(setName)} maxLength={120}
              autoComplete="name" readOnly={readOnly} />
          </div>
          <div className="ac-field">
            <label htmlFor="ac-loc" className="ac-label">{COPY.location}</label>
            <input id="ac-loc" className="ac-input" value={loc} onChange={edited(setLoc)} maxLength={80}
              autoComplete="address-level2" readOnly={readOnly} />
          </div>
        </div>

        <div className="ac-field">
          <label htmlFor="ac-email" className="ac-label">{COPY.email}</label>
          {/* while a code is on its way the login is still the old address, so the field says so */}
          <input id="ac-email" ref={emailRef} className="ac-input" type="email" value={waiting ? person.email : email}
            onChange={e => { setEmail(e.target.value); setSaved(false); setMail(m => (m.step === 'done' ? { step: 'idle', error: '' } : { ...m, error: '' })) }}
            autoComplete="email" inputMode="email" autoCapitalize="none" spellCheck="false"
            readOnly={readOnly || shared || waiting} aria-describedby="ac-email-hint" />
          <span id="ac-email-hint" className="ac-hint">{shared ? COPY.sharedEmail : COPY.emailHint}</span>
          {mail.step === 'done' && <p className="ac-msg" role="status">{mail.line}</p>}
          {!waiting && mail.error && <p className="ac-msg ac-msg--error" role="alert">{mail.error}</p>}
        </div>

        {acc && (
          <div className="ac-field">
            <label htmlFor="ac-about" className="ac-label">{dirWords.about}</label>
            <textarea id="ac-about" className="ac-input ac-textarea" value={about} onChange={edited(setAbout)} maxLength={400}
              readOnly={readOnly} />
          </div>
        )}
        {acc && (dirWords.switches.length > 1
          ? <div className="ac-switches">{dirWords.switches.map(w => switchRow(w))}</div>
          : switchRow(dirWords.switches[0]))}
        {acc && rows.status === 'error' && <p className="ac-msg ac-msg--error" role="alert">{COPY.listingsFailed}</p>}

        {waiting && (
          <div className="ac-code" role="group" aria-labelledby="ac-code-line">
            <p id="ac-code-line" className="ac-msg">{mail.line}</p>
            <label htmlFor="ac-code" className="ac-label">{COPY.codeLabel}</label>
            <div className="ac-code-row">
              <input id="ac-code" ref={codeRef} className="ac-input ac-code-input" inputMode="numeric" autoComplete="one-time-code"
                value={code} readOnly={readOnly}
                onChange={e => setCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 8))}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); confirm() } }} />
              <button type="button" className="ac-btn ac-btn--ink" onClick={confirm} disabled={readOnly || codeBusy} aria-busy={codeBusy || undefined}>
                {COPY.confirmEmail}
              </button>
            </div>
            {mail.error && <p className="ac-msg ac-msg--error" role="alert">{mail.error}</p>}
            <div className="ac-links">
              <button type="button" className="ac-link" onClick={resend} disabled={readOnly || codeBusy}>{mail.resent ? COPY.resent : COPY.resend}</button>
              <button type="button" className="ac-link" onClick={differentEmail} disabled={readOnly}>{COPY.differentEmail}</button>
            </div>
          </div>
        )}

        <div className="ac-row ac-row--save">
          {/* greyed out while a code waits, so Change email is the 1 black button (Adam, 3 Oct) */}
          <button type="submit" className="ac-btn ac-btn--ink" disabled={readOnly || busy || waiting} aria-busy={busy || undefined}>{COPY.save}</button>
          <span className="ac-msg ac-row-msg" role="status">{saved ? COPY.saved : ''}</span>
        </div>
        {error && <p className="ac-msg ac-msg--error" role="alert">{error}</p>}
      </form>
    </section>
  )
}
